import React from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Menu } from "@design-system/Menu/Menu.lib";
import { AlertCircleIcon, UploadIcon } from "@icons";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import styles from "./MigrationFileUploadTaskBody.module.scss";

interface AdoptFileStartDateAction {
    label: string;
    onAdopt: () => void;
    isAdopting: boolean;
}

interface MigrationCommandBarProps {
    finalizeWarningMessages: string[];
    finalizeDisabledReason: string | undefined;
    canFinalize: boolean;
    isMigrating: boolean;
    onUploadDocument: () => void;
    onBrowseDocuments: () => void;
    onFinalize: () => void;
    extraActions?: React.ReactNode;
    adoptFileStartDate?: AdoptFileStartDateAction;
}

export const MigrationCommandBar: React.FC<MigrationCommandBarProps> = ({
    finalizeWarningMessages,
    finalizeDisabledReason,
    canFinalize,
    isMigrating,
    onUploadDocument,
    onBrowseDocuments,
    onFinalize,
    extraActions,
    adoptFileStartDate,
}) => {
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const finalizeReason = useViewOnlyTooltipReason(
        finalizeDisabledReason ?? "",
    );

    const handleAdopt = () => {
        if (refuseIfViewOnly()) {
            return;
        }
        adoptFileStartDate?.onAdopt();
    };

    const handleFinalize = () => {
        if (refuseIfViewOnly()) {
            return;
        }
        onFinalize();
    };

    return (
        <CommandIsland sticky className={styles.commandIsland}>
            {finalizeWarningMessages.length > 0 && (
                <p className={styles.commandIslandWarning}>
                    <AlertCircleIcon
                        width={14}
                        height={14}
                        className="icon-color-yellow-600"
                    />
                    {finalizeWarningMessages[0]}
                    {adoptFileStartDate && (
                        <ButtonWithLoader
                            variant="tertiary"
                            size="sm"
                            loading={adoptFileStartDate.isAdopting}
                            disabled={isMigrating}
                            onClick={handleAdopt}
                        >
                            {adoptFileStartDate.label}
                        </ButtonWithLoader>
                    )}
                </p>
            )}
            <div className={styles.commandIslandSpacer} />
            <div className={styles.commandBarActions}>
                {extraActions}
                <Menu.Root
                    trigger={
                        <Button
                            variant="primary"
                            size="md"
                            disabled={isMigrating}
                            leftIcon={<UploadIcon />}
                        >
                            Upload
                        </Button>
                    }
                >
                    <Menu.Item onClick={onUploadDocument}>
                        Upload document
                    </Menu.Item>
                    <Menu.Item onClick={onBrowseDocuments}>
                        Browse from documents
                    </Menu.Item>
                </Menu.Root>
                <Tooltip content={finalizeReason}>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        onClick={handleFinalize}
                        loading={isMigrating}
                        disabled={!canFinalize}
                    >
                        Import
                    </ButtonWithLoader>
                </Tooltip>
            </div>
        </CommandIsland>
    );
};
